import { Controller } from '@hotwired/stimulus';
import { STATUS, statusFor, applyStatusClass } from './helpers/undistractor_status';
import UndistractorOptionStore from '../stores/undistractor_option_store';

export default class extends Controller {
  static targets = ['container', 'template', 'emptyState'];

  connect() {
    this.store = UndistractorOptionStore.getInstance();
    this.unsubscribe = this.store.subscribeAll((allOptions) => {
      this.renderSidebar(allOptions);
    });
  }

  disconnect() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  renderSidebar(allOptions) {
    allOptions.forEach((option) => {
      this.syncSidebarItem(option);
    });
    this.#updateEmptyState();
  }

  syncSidebarItem(option) {
    const hasWindow = !!(option.schedule && option.schedule !== '');
    const isActiveOrScheduled = option.active || hasWindow;
    let row = this.#getRowFromUndistractorOptionId(option.id);

    if (!row && isActiveOrScheduled) {
      const displayOption = {
        ...option,
        undistractor_option_id: option.id,
        icon_background: option.color,
      };
      row = this.#injectValues(displayOption);
    }

    if (!row) {
      return;
    }

    this.#injectTemplateValues(row, 'schedule', option.schedule);
    // Reflect the shared store via the same status classes the main row uses — the reused switch /
    // toggle / window CSS renders the rest. has-window reveals the toggle (new windows are created
    // from the main table, so a tool with no window only shows the switch).
    this.#setStatus(row, option);
    row.classList.toggle('has-window', hasWindow);
    this.#setRowOpacity(row, isActiveOrScheduled);
    this.#setIconUncolor(row, isActiveOrScheduled);
  }

  // The controls are proxies: clicking one clicks the matching control on the main-table row, which
  // owns the real logic (optimistic apply, server sync, editor). The store update flows back here.
  toggleActiveState(event) {
    this.#clickMainTableElement(event, '.undistractor-checkbox');
  }

  toggleActiveStateByEnter(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleActiveState(event);
    }
  }

  // The pill is one toggle button; proxy its click to the main row's, which owns the logic.
  toggleMode(event) {
    this.#clickMainTableElement(event, '.undistractor-card-seg');
  }

  // The switch is a div, so its state and name have to be spelled out for assistive tech — and the
  // name only exists once the store's values are injected, which is why it is set here rather than
  // in the template.
  #setStatus(row, option) {
    const status = statusFor(option);
    applyStatusClass(row, status);

    const toggle = row.querySelector('.undistractor-sidebar-switch');
    if (!toggle) return;

    toggle.setAttribute('aria-checked', String(status !== STATUS.INACTIVE));
    if (option.undistractor_name && option.undistractable_name) {
      toggle.setAttribute('aria-label', `${option.undistractor_name} on ${option.undistractable_name}`);
    }
  }

  #clickMainTableElement(event, selector) {
    const element = this.#getMainTableRow(event)?.querySelector(selector);
    element?.click();
  }

  #getMainTableRow(event) {
    const row = event.target.closest('.active-sidebar-undistractor-row');
    const undistractorOptionId = row.dataset.undistractorOptionId;

    return document.querySelector(`#undistractor-option-row-${undistractorOptionId}`);
  }

  #getRowFromUndistractorOptionId(undistractor_option_id) {
    return this.containerTarget.querySelector(`.active-sidebar-undistractor-row[data-undistractor-option-id="${undistractor_option_id}"]`);
  }

  #injectValues(undistractorOption) {
    const template = this.templateTarget.content.cloneNode(true);

    for (const [key, value] of Object.entries(undistractorOption)) {
      this.#injectTemplateValues(template, key, value);
    }

    this.containerTarget.appendChild(template);
    const element = this.containerTarget.lastElementChild;
    return element;
  }

  #injectTemplateValues(parent, fieldName, value) {
    const element = parent.querySelector(`[data-field="${fieldName}"]`);

    if (!element) return;

    if (fieldName === 'undistractor_option_id') {
      element.setAttribute('data-undistractor-option-id', value || '');
    } else if (fieldName === 'icon_background') {
      element.classList.add(`bg-${value}-light`);
    } else if (element.tagName === 'IMG') {
      element.src = value || '';
      element.style.display = value ? '' : 'none';
    } else {
      element.textContent = value || '';
    }
  }

  #setRowOpacity(target, checked) {
    const titleBox = target.querySelector('.active-undistractors-sidebar-text');
    if (checked) {
      titleBox.classList.remove('opacity-50');
    } else {
      titleBox.classList.add('opacity-50');
    }
  }

  #setIconUncolor(row, isActiveOrScheduled) {
    const icon = row.querySelector('.undistractor-settings-icon');
    if (!icon) return;

    if (isActiveOrScheduled) {
      icon.classList.remove('uncolor');
    } else {
      icon.classList.add('uncolor');
    }
  }

  #updateEmptyState() {
    if (!this.hasEmptyStateTarget) return;

    const hasRows = this.containerTarget.children.length > 0;
    if (hasRows) {
      this.emptyStateTarget.classList.add('d-none');
    } else {
      this.emptyStateTarget.classList.remove('d-none');
    }
  }
}
